import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/">
            <Button variant="outline" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600 mb-8">
              <strong>Effective Date:</strong> December 2025<br />
              <strong>Last Updated:</strong> December 2025
            </p>

            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Introduction</h2>
                <p>
                  Welcome to Livington Designs ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="mt-3">
                  By using our website or services, you agree to the collection and use of information in accordance with this policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.1 Personal Information</h3>
                <p>When you request a free estimate or contact us, we may collect:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Full name</li>
                  <li>Phone number</li>
                  <li>Email address</li>
                  <li>Property address</li>
                  <li>Property size and details</li>
                  <li>Preferred start date</li>
                  <li>Service requirements and project details</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.2 Automatically Collected Information</h3>
                <p>When you visit our website, we may automatically collect:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website</li>
                  <li>Geographic location (city/state level)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
                <p>We use the collected information for the following purposes:</p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>To respond to your estimate requests and inquiries</li>
                  <li>To schedule consultations and painting services</li>
                  <li>To provide written estimates and project proposals</li>
                  <li>To communicate about your project status</li>
                  <li>To send service-related updates and information</li>
                  <li>To improve our website and services</li>
                  <li>To comply with legal obligations</li>
                  <li>To prevent fraud and enhance security</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. How We Share Your Information</h2>
                <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our business (e.g., email service providers, scheduling tools)</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government regulation</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity</li>
                  <li><strong>With Your Consent:</strong> We may share information with your explicit consent for specific purposes</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Secure data transmission (SSL/TLS encryption)</li>
                  <li>Secure data storage with access controls</li>
                  <li>Regular security assessments</li>
                  <li>Employee training on data protection</li>
                </ul>
                <p className="mt-3">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Data Retention</h2>
                <p>
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Estimate requests and customer information may be retained for:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Active customers: Duration of service relationship plus 7 years</li>
                  <li>Estimate requests (not converted): Up to 2 years</li>
                  <li>Marketing communications: Until you opt out or request deletion</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Your Privacy Rights</h2>
                <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time</li>
                  <li><strong>Data Portability:</strong> Request transfer of your data to another service provider</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                </ul>
                <p className="mt-3">
                  To exercise any of these rights, please contact us at <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">info@livingtone.store</a> or call <a href="tel:+18717871113" className="text-blue-900 hover:underline">+1-871-7871-113</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Cookies and Tracking Technologies</h2>
                <p>
                  Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Remember your preferences</li>
                  <li>Understand how you use our website</li>
                  <li>Improve website functionality</li>
                  <li>Analyze website traffic and performance</li>
                </ul>
                <p className="mt-3">
                  You can control cookies through your browser settings. However, disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Children's Privacy</h2>
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete it.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">11. California Privacy Rights</h2>
                <p>
                  If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Right to know what personal information is collected</li>
                  <li>Right to know if personal information is sold or disclosed</li>
                  <li>Right to opt-out of the sale of personal information</li>
                  <li>Right to deletion of personal information</li>
                  <li>Right to non-discrimination for exercising CCPA rights</li>
                </ul>
                <p className="mt-3">
                  <strong>Note:</strong> We do not sell personal information to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Posting the updated policy on our website</li>
                  <li>Updating the "Last Updated" date</li>
                  <li>Sending email notification for significant changes (if you've provided your email)</li>
                </ul>
                <p className="mt-3">
                  Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Contact Us</h2>
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mt-4">
                  <p><strong>Livington Designs</strong></p>
                  <p className="mt-2">4512 Sunset Boulevard<br />Los Angeles, CA 90027</p>
                  <p className="mt-2">Phone: <a href="tel:+18717871113" className="text-blue-900 hover:underline">+1-871-7871-113</a></p>
                  <p>Email: <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">info@livingtone.store</a></p>
                </div>
              </section>

              <section className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-gray-600">
                  By using our website and services, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Livington Designs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
